import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import styles from './ServiceDetail.module.css';
import ServiceContactForm from '../components/ServiceContactForm';
import skilledNursing from '../assets/images/skilled-nursing.png';
import physicalTherapy from '../assets/images/physical-therapy.png';
import ocupationTherapy from '../assets/images/ocupation-therapy.png';
import speechTherapy from '../assets/images/speech-therapy.png';
import socialService from '../assets/images/social-service.png';
import demantiaHelp from '../assets/images/demantia-help.png';
import geriatricCare from '../assets/images/geriatric-care.png';
import medicationReminders from '../assets/images/medication-reminders.png';
import postHospitalizationCare from '../assets/images/post-hospitalization-care.png';
import about1 from '../assets/images/about-1.png';
import { FaUserNurse, FaWalking, FaHandHoldingHeart, FaComments, FaUsers, FaHome, FaBrain, FaClipboardList, FaPills, FaHospital, FaArrowRight, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import SEO from '../components/SEO';

const ServiceDetail = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
  const [isTablet, setIsTablet] = useState(window.innerWidth > 767 && window.innerWidth <= 991);
  const sliderRef = useRef(null);
  const autoScrollRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 767);
      setIsTablet(window.innerWidth > 767 && window.innerWidth <= 991);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // All services
  const allServices = [
    {
      id: 1,
      title: 'Skilled Nursing',
      description: 'Registered nurses provide skilled care and instruction to patients and families, including wound care, medication supervision, catheter care, treatments and injections, and I.V. therapy.',
      image: skilledNursing,
      icon: <FaUserNurse />,
      link: '/services/skilled-nursing'
    },
    {
      id: 2,
      title: 'Physical Therapy',
      description: 'Licensed physical therapists help patients regain strength, mobility, and independence through personalized treatment plans.',
      image: physicalTherapy,
      icon: <FaWalking />,
      link: '/services/physical-therapy'
    },
    {
      id: 3,
      title: 'Occupational Therapy',
      description: 'Occupational therapists assist patients in developing, recovering, and maintaining daily living skills for improved quality of life.',
      image: ocupationTherapy,
      icon: <FaHandHoldingHeart />,
      link: '/services/occupational-therapy'
    },
    {
      id: 4,
      title: 'Speech Therapy',
      description: 'Speech-language pathologists help patients improve communication, swallowing difficulties, and cognitive skills.',
      image: speechTherapy,
      icon: <FaComments />,
      link: '/services/speech-therapy'
    },
    {
      id: 5,
      title: 'Social Services',
      description: 'Social workers provide counseling, community resource connections, and support for patients and families navigating healthcare challenges.',
      image: socialService,
      icon: <FaUsers />,
      link: '/services/social-services'
    },
    {
      id: 6,
      title: 'Certified Home Health Aid',
      description: 'Certified aides assist patients with personal care, meal preparation, ambulation, and exercises under professional supervision to support independent living at home.',
      image: about1,
      icon: <FaHome />,
      link: '/services/certified-home-health-aid'
    },
    {
      id: 7,
      title: 'Alzheimer\'s Care',
      description: 'Specialized care and support for patients with Alzheimer\'s disease and dementia, focusing on safety, dignity, and quality of life.',
      image: demantiaHelp,
      icon: <FaBrain />,
      link: '/services/alzheimers-care'
    },
    {
      id: 8,
      title: 'Geriatric Care Management',
      description: 'Comprehensive care coordination and advocacy services to help seniors and their families navigate complex healthcare and social service systems.',
      image: geriatricCare,
      icon: <FaClipboardList />,
      link: '/services/geriatric-care-management'
    },
    {
      id: 9,
      title: 'Medication Reminders',
      description: 'Professional medication management services to ensure proper medication adherence and prevent missed doses or dangerous interactions.',
      image: medicationReminders,
      icon: <FaPills />,
      link: '/services/medication-reminders'
    },
    {
      id: 10,
      title: 'Post-hospitalization Care',
      description: 'Transitional care services to support safe recovery at home after hospital discharge, reducing readmission risks and promoting healing.',
      image: postHospitalizationCare,
      icon: <FaHospital />,
      link: '/services/post-hospitalization-care'
    }
  ];

  // Filter out current service (Skilled Nursing in this case)
  const relatedServices = allServices.filter(service => service.id !== 1);

  const getMaxIndex = () => {
    if (isMobile) return relatedServices.length - 1;
    if (isTablet) return relatedServices.length - 2;
    return relatedServices.length - 3;
  };

  // Auto-scroll functionality
  useEffect(() => {
    const maxIndex = getMaxIndex();

    const interval = setInterval(() => {
      setCurrentIndex((prev) => {
        if (prev >= maxIndex) return 0;
        return prev + 1;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [isMobile, isTablet, relatedServices.length]);

  const nextSlide = () => {
    const maxIndex = getMaxIndex();
    setCurrentIndex((prev) => {
      if (prev >= maxIndex) return 0;
      return prev + 1;
    });
  };

  const prevSlide = () => {
    const maxIndex = getMaxIndex();
    setCurrentIndex((prev) => {
      if (prev <= 0) return maxIndex;
      return prev - 1;
    });
  };

  return (
    <div className={styles.serviceDetailPage}>
      <SEO
        title="Skilled Nursing Services"
        description="Professional skilled nursing care at home in Southern California. Registered nurses provide wound care, medication supervision, catheter care, IV therapy, and more."
        keywords="skilled nursing, registered nurse, wound care, IV therapy, medication management, catheter care, home nursing"
        canonicalUrl="https://allforyouhc.com/services/skilled-nursing"
      />
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroContainer}>
          <div className={styles.heroContent}>
            <div className={styles.iconBadge}>
              <FaUserNurse />
            </div>
            <p className={styles.heroTag}>SENIOR CARE SERVICES</p>
            <h1 className={styles.heroTitle}>Skilled Nursing</h1>
            <p className={styles.heroDescription}>
              Professional nursing care delivered by registered nurses in the comfort of your home.
              Our skilled nurses provide expert medical care, health monitoring, and patient education
              to support recovery and maintain optimal health.
            </p>
            <Link to="/contact" className={styles.heroButton}>
              Get Started
            </Link>
          </div>
          <div className={styles.heroImageContainer}>
            <img src={skilledNursing} alt="Skilled Nursing" className={styles.heroImage} />
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className={styles.includedSection}>
        <div className={styles.container}>
          <div className={styles.includedGrid}>
            <div className={styles.includedContent}>
              <h2 className={styles.sectionTitle}>What's included in the service?</h2>
              <p className={styles.includedText}>
                Our skilled nursing services encompass a wide range of medical care and support
                designed to help you recover, manage chronic conditions, and maintain your health
                at home. Each care plan is customized to meet your individual needs.
              </p>
              <ul className={styles.includedList}>
                <li>Wound care and dressing changes for surgical sites and chronic wounds</li>
                <li>Medication management, administration, and patient education</li>
                <li>Catheter care and management of urinary drainage systems</li>
                <li>IV therapy administration and monitoring</li>
                <li>Injection administration including insulin and other medications</li>
                <li>Vital signs monitoring and health status assessment</li>
                <li>Pain management and symptom control</li>
                <li>Patient and family education on disease management</li>
                <li>Coordination with physicians and other healthcare providers</li>
              </ul>
            </div>
            <div className={styles.includedImage}>
              <img src={about1} alt="Nursing Care" />
            </div>
          </div>
        </div>
      </section>

      {/* About Service Section */}
      <section className={styles.aboutSection}>
        <div className={styles.container}>
          <div className={styles.aboutGrid}>
            <div className={styles.aboutContent}>
              <h2 className={styles.sectionTitle}>About this service</h2>
              <p className={styles.aboutText}>
                Our Skilled Nursing services provide comprehensive medical care in the comfort and
                privacy of your own home. Our registered nurses are highly trained professionals
                who work closely with your physician to deliver personalized care tailored to your
                specific health needs.
              </p>
              <p className={styles.aboutText}>
                Whether you're recovering from surgery, managing a chronic condition, or need ongoing
                medical supervision, our skilled nurses are equipped to handle complex medical procedures
                and provide the highest quality of care. We focus on not just treating symptoms, but
                educating patients and families to promote long-term wellness and independence.
              </p>
            </div>
            <div className={styles.contactCard}>
              <h3 className={styles.contactTitle}>Get in contact with us!</h3>
              <p className={styles.contactText}>
                Ready to learn more about our skilled nursing services? Contact us today to discuss
                your specific needs and how we can help.
              </p>
              <ServiceContactForm
                serviceName="Skilled Nursing"
                serviceType="Skilled Nursing Services"
              />
            </div>
          </div>
        </div>
      </section>

      {/* More Services Section */}
      <section className={styles.moreServicesSection}>
        <div className={styles.container}>
          <div className={styles.moreServicesHeader}>
            <h2 className={styles.sectionTitle}>More services</h2>
            <Link to="/services" className={styles.browseButton}>
              Browse services
            </Link>
          </div>
          <div className={styles.sliderWrapper}>
            <button
              className={styles.navButton + ' ' + styles.navButtonPrev}
              onClick={prevSlide}
              aria-label="Previous services"
            >
              <FaChevronLeft />
            </button>

            <div className={styles.sliderContainer}>
              <div
                className={styles.servicesSlider}
                style={{
                  transform: isMobile
                    ? `translateX(calc(-${currentIndex * 100}% - ${currentIndex * 16}px))`
                    : isTablet
                    ? `translateX(calc(-${currentIndex * 50}% - ${currentIndex * 12}px))`
                    : `translateX(calc(-${currentIndex * 33.333}% - ${currentIndex * 10.67}px))`,
                  transition: 'transform 0.5s ease-in-out'
                }}
              >
                {relatedServices.map((service) => (
                  <div key={service.id} className={styles.slideItem}>
                    <Link to={service.link} className={styles.serviceCard}>
                      <div className={styles.serviceImageContainer}>
                        <img src={service.image} alt={service.title} className={styles.serviceImage} />
                        <div className={styles.serviceIconBadge}>{service.icon}</div>
                      </div>
                      <div className={styles.serviceContent}>
                        <h3 className={styles.serviceTitle}>{service.title}</h3>
                        <p className={styles.serviceDescription}>{service.description}</p>
                        <div className={styles.serviceLink}>
                          <span>View service</span>
                          <FaArrowRight />
                        </div>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
            </div>

            <button
              className={styles.navButton + ' ' + styles.navButtonNext}
              onClick={nextSlide}
              aria-label="Next services"
            >
              <FaChevronRight />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
